import { useEffect, useMemo } from 'react'

import { StoryPanels } from '@/components/panels/StoryPanels.tsx'
import { useQueryCurrentIteration } from '@/requests/queries/useQueryProject.ts'
import { useQuerySearch } from '@/requests/queries/useQuerySearch.ts'
import { isAccepted, isInProgress, isUnscheduled, isUnstarted } from '@/utils/status.ts'

type Props = {
  filter: SearchFilter | null
  isShowDone: boolean
  isShowRelevant: boolean
  isShowUnscheduled: boolean
}

export const SearchStoriesView = ({ filter, isShowDone, isShowRelevant, isShowUnscheduled }: Props) => {
  const { search, result, loading, error } = useQuerySearch()
  const { currentIteration } = useQueryCurrentIteration()

  useEffect(() => {
    if (filter) {
      search(filter)
    }
  }, [filter, search])

  const done = useMemo(
    () =>
      result?.stories
        .filter((story) => isAccepted(story.status) && (story.iterationNumber ?? 1) < currentIteration.number)
        .toSorted((a, b) =>
          (a.acceptedAt ?? Number.POSITIVE_INFINITY) <= (b.acceptedAt ?? Number.POSITIVE_INFINITY) ? -1 : 1,
        ) ?? [],
    [result, currentIteration],
  )
  const relevant = useMemo(
    () =>
      result?.stories.filter(
        (story) =>
          (isAccepted(story.status) || isInProgress(story.status) || isUnstarted(story.status)) &&
          (story.iterationNumber ?? 1) >= currentIteration.number,
      ) ?? [],
    [result, currentIteration],
  )
  const unscheduled = useMemo(() => result?.stories.filter((story) => isUnscheduled(story.status)) ?? [], [result])

  if (loading) {
    return <div>Loading...</div>
  }

  if (error) {
    return <div>Error: {error.message}</div>
  }

  return (
    <StoryPanels
      done={done}
      relevant={relevant}
      unscheduled={unscheduled}
      isShowDone={isShowDone}
      isShowRelevant={isShowRelevant}
      isShowUnscheduled={isShowUnscheduled}
    />
  )
}

type SearchSummaryProps = {
  filter: SearchFilter | null
}

export const SearchSummary = ({ filter }: SearchSummaryProps) => {
  const filterText = useMemo(
    () =>
      filter
        ? Object.entries(filter)
            .filter(([key]) => key !== 'projectId')
            .map(([key, value]) => (key === 'query' ? value : `${key}: ${value}`))
            .join(', ')
        : '',
    [filter],
  )

  return (
    <p className="font-EN-KR-body1-bold text-contents-secondary gap-2 pl-2">
      <span className="text-contents-secondary">Search Results : </span>
      <span className="text-contents-primary">“{filterText}”</span>
    </p>
  )
}
